/**
 * Production-Safe Performance Metrics
 * 
 * Lightweight instrumentation for critical user journeys:
 * - Cold start → first usable screen
 * - Tab switch timing
 * - Image loading impact on LCP
 * 
 * In DEV: logs to console with detailed breakdowns
 * In PROD: metrics stored silently (can be sent to analytics)
 */

const IS_DEV = import.meta.env.DEV;

// Store for performance entries
interface PerfEntry {
  name: string;
  startTime: number;
  duration?: number;
  data?: Record<string, unknown>;
}

interface ColdStartMetrics {
  route_start: number;
  first_shell_render?: number;
  auth_ready?: number;
  data_fetch_start?: number;
  data_fetch_end?: number;
  lcp_proxy?: number;
  total_duration?: number;
}

interface TabSwitchMetrics {
  tab: string;
  switch_start: number;
  chunk_ready?: number;
  data_ready?: number;
  first_paint?: number;
  total_duration?: number;
  was_cached: boolean;
}

// Global metrics store
const metricsStore = {
  coldStart: null as ColdStartMetrics | null,
  tabSwitches: [] as TabSwitchMetrics[],
  renderCounts: new Map<string, number>(),
};

// Expose for DevTools inspection
if (typeof window !== 'undefined') {
  (window as Window & { __LLX_METRICS__?: typeof metricsStore }).__LLX_METRICS__ = metricsStore;
}

// ==================== Cold Start Tracking ====================

let coldStartActive = false;

export function startColdStartTracking() {
  if (metricsStore.coldStart) return; // Already started
  
  coldStartActive = true;
  metricsStore.coldStart = {
    route_start: performance.now(),
  };
  
  if (IS_DEV) {
    console.log('%c[PERF] Cold start tracking began', 'color: #8B5CF6; font-weight: bold;');
  }
}

export function markColdStart(mark: keyof Omit<ColdStartMetrics, 'route_start' | 'total_duration'>) {
  if (!metricsStore.coldStart || !coldStartActive) return;
  
  const now = performance.now();
  metricsStore.coldStart[mark] = now;
  
  // Signal to route preloader that shell has rendered
  if (mark === 'first_shell_render') {
    import('@/hooks/useRoutePreloader').then(({ markFirstShellRendered }) => {
      markFirstShellRendered();
    });
  }
  
  // Auto-complete when LCP is reached
  if (mark === 'lcp_proxy') {
    metricsStore.coldStart.total_duration = now - metricsStore.coldStart.route_start;
    coldStartActive = false;
    logColdStartMetrics();
  }
}

function logColdStartMetrics() {
  if (!IS_DEV || !metricsStore.coldStart) return;
  
  const cs = metricsStore.coldStart;
  const start = cs.route_start;
  
  const breakdown = {
    'Phase': 'Duration (ms)',
    'To Shell': cs.first_shell_render ? Math.round(cs.first_shell_render - start) : '-',
    'Shell → Auth Ready': cs.auth_ready && cs.first_shell_render 
      ? Math.round(cs.auth_ready - cs.first_shell_render) : '-',
    'Auth → Data Start': cs.data_fetch_start && cs.auth_ready 
      ? Math.round(cs.data_fetch_start - cs.auth_ready) : '-',
    'Data Fetch': cs.data_fetch_end && cs.data_fetch_start 
      ? Math.round(cs.data_fetch_end - cs.data_fetch_start) : '-',
    'To LCP': cs.lcp_proxy ? Math.round(cs.lcp_proxy - start) : '-',
    '─────': '─────',
    'TOTAL': cs.total_duration ? Math.round(cs.total_duration) : '-',
  };
  
  console.log(
    '%c[PERF] Cold Start Complete',
    'background: #22c55e; color: white; padding: 2px 8px; border-radius: 4px; font-weight: bold;'
  );
  console.table(breakdown);
  
  // Identify dominant bottleneck
  const phases = [
    { name: 'Shell Render', ms: cs.first_shell_render ? cs.first_shell_render - start : 0 },
    { name: 'Auth Bootstrap', ms: cs.auth_ready && cs.first_shell_render ? cs.auth_ready - cs.first_shell_render : 0 },
    { name: 'Data Fetch', ms: cs.data_fetch_end && cs.data_fetch_start ? cs.data_fetch_end - cs.data_fetch_start : 0 },
    { name: 'Render to LCP', ms: cs.lcp_proxy && cs.data_fetch_end ? cs.lcp_proxy - cs.data_fetch_end : 0 },
  ].filter(p => p.ms > 0);
  
  const total = phases.reduce((sum, p) => sum + p.ms, 0);
  const dominant = phases.reduce((max, p) => p.ms > max.ms ? p : max, { name: 'None', ms: 0 });
  
  if (dominant.ms > 0 && total > 0) {
    const pct = Math.round((dominant.ms / total) * 100);
    console.log(
      `%c[PERF] Dominant bottleneck: ${dominant.name} (${pct}% of total)`,
      pct > 50 ? 'color: #ef4444; font-weight: bold;' : 'color: #f59e0b;'
    );
  }
}

// ==================== Tab Switch Tracking ====================

let activeTabSwitch: TabSwitchMetrics | null = null;

export function startTabSwitchTracking(tab: string, wasCached: boolean = false) {
  activeTabSwitch = {
    tab,
    switch_start: performance.now(),
    was_cached: wasCached,
  };
}

export function markTabSwitch(mark: 'chunk_ready' | 'data_ready' | 'first_paint') {
  if (!activeTabSwitch) return;
  
  const now = performance.now();
  activeTabSwitch[mark] = now;
  
  // Complete on first_paint
  if (mark === 'first_paint') {
    activeTabSwitch.total_duration = now - activeTabSwitch.switch_start;
    metricsStore.tabSwitches.push({ ...activeTabSwitch });
    logTabSwitchMetrics(activeTabSwitch);
    activeTabSwitch = null;
  }
}

function logTabSwitchMetrics(metrics: TabSwitchMetrics) {
  if (!IS_DEV) return;
  
  const start = metrics.switch_start;
  const total = metrics.total_duration || 0;
  const status = total < 100 ? '✓ INSTANT' : total < 300 ? '✓ FAST' : '⚠ SLOW';
  const statusColor = total < 100 ? '#22c55e' : total < 300 ? '#f59e0b' : '#ef4444';
  
  console.log(
    `%c[TAB] ${metrics.tab} ${status} (${Math.round(total)}ms)${metrics.was_cached ? ' [CACHED]' : ''}`,
    `background: ${statusColor}; color: white; padding: 2px 6px; border-radius: 4px; font-weight: bold;`
  );
  
  if (total >= 300) {
    console.log('  Chunk:', metrics.chunk_ready ? Math.round(metrics.chunk_ready - start) + 'ms' : '-');
    console.log('  Data:', metrics.data_ready ? Math.round(metrics.data_ready - start) + 'ms' : '-');
    console.log('  Paint:', metrics.first_paint ? Math.round(metrics.first_paint - start) + 'ms' : '-');
  }
}

// ==================== Render Counting ====================

export function countRender(componentName: string) {
  if (!IS_DEV) return;
  
  const current = metricsStore.renderCounts.get(componentName) || 0;
  metricsStore.renderCounts.set(componentName, current + 1);
}

export function logRenderCounts(filter?: string) {
  if (!IS_DEV) return;
  
  const entries = Array.from(metricsStore.renderCounts.entries())
    .filter(([name]) => !filter || name.includes(filter))
    .sort((a, b) => b[1] - a[1]);
  
  console.log('%c[PERF] Render Counts', 'color: #8B5CF6; font-weight: bold;');
  console.table(entries.slice(0, 20).map(([name, count]) => ({ Component: name, Renders: count })));
}

export function resetRenderCounts() {
  metricsStore.renderCounts.clear();
}

// ==================== LCP Observer ====================

let lcpObserver: PerformanceObserver | null = null;

export function observeLCP(callback?: (lcpMs: number) => void) {
  if (typeof PerformanceObserver === 'undefined') return;
  
  try {
    lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1] as PerformanceEntry & { startTime: number };
      
      if (lastEntry) {
        markColdStart('lcp_proxy');
        callback?.(lastEntry.startTime);
        
        if (IS_DEV) {
          console.log(
            `%c[LCP] ${Math.round(lastEntry.startTime)}ms`,
            lastEntry.startTime < 2500 ? 'color: #22c55e; font-weight: bold;' : 'color: #ef4444; font-weight: bold;'
          );
        }
      }
    });
    
    lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
  } catch {
    // LCP observation not supported
  }
}

export function disconnectLCPObserver() {
  lcpObserver?.disconnect();
  lcpObserver = null;
}

// ==================== Summary Report ====================

export function getPerfSummary() {
  const cs = metricsStore.coldStart;
  const tabSwitches = metricsStore.tabSwitches;
  
  // Calculate average tab switch times
  const avgTabSwitch = tabSwitches.length > 0
    ? tabSwitches.reduce((sum, t) => sum + (t.total_duration || 0), 0) / tabSwitches.length
    : 0;
  
  const cachedSwitches = tabSwitches.filter(t => t.was_cached);
  const avgCachedSwitch = cachedSwitches.length > 0
    ? cachedSwitches.reduce((sum, t) => sum + (t.total_duration || 0), 0) / cachedSwitches.length
    : 0;
  
  return {
    coldStart: {
      total_ms: cs?.total_duration ? Math.round(cs.total_duration) : null,
      auth_ready_ms: cs?.auth_ready ? Math.round(cs.auth_ready - cs.route_start) : null,
      lcp_ms: cs?.lcp_proxy ? Math.round(cs.lcp_proxy - cs.route_start) : null,
    },
    tabSwitches: {
      count: tabSwitches.length,
      avg_ms: Math.round(avgTabSwitch),
      avg_cached_ms: Math.round(avgCachedSwitch),
    },
    renderCounts: Object.fromEntries(metricsStore.renderCounts),
  };
}

// Auto-start cold start tracking on module load
startColdStartTracking();
